import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { IScriptAndStyle } from "../../../models/Interfaces";
import { useConfirm } from "../../../config/ConfirmProvider";
import { CellProps, Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Nav, Spinner } from "react-bootstrap";
import { faEdit, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import Loading from "../../components/common/Loading";
import FilteredTable from "../../components/common/FilteredTable";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import { ScriptAndStyleService } from "../../../services/ScriptAndStyleService";
import { ScriptIncludeType } from "../../../config/Enums";
import AddUpdateScriptCss from "./AddUpdateScriptCss";
import styles from "./scriptsAndStyles.module.scss";

class ScriptTabs {
  public static Css: string = "CSS";
  public static JS: string = "JS";
  public static Robots: string = "ROBOTS";
  public static AI: string = "AI";
};

type ScriptTabMeta = {
  title: string;
  sectionTitle: string;
  summary: string;
  addText: string;
  emptyMessage: string;
  type: ScriptIncludeType;
};

const ScriptsAndStyles = () => {
  const [scripts, setScripts] = useState<Array<IScriptAndStyle>>([]);
  const [styleItems, setStyleItems] = useState<Array<IScriptAndStyle>>([]);
  const [robots, setRobots] = useState<Array<IScriptAndStyle>>([]);
  const [aiFiles, setAiFiles] = useState<Array<IScriptAndStyle>>([]);

  const [selected, setSelected] = useState<IScriptAndStyle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(ScriptTabs.Css);

  const [action, setAction] = useState("");
  const actionRef = useRef(false);

  const notify = (success: boolean, message: string): void => {
    actionEmitter.emit(EmitEvent.Result, success, message);
  };

  const runAction = async (label: string, work: () => Promise<void>): Promise<void> => {
    if (actionRef.current) return;
    actionRef.current = true;
    setAction(label);
    try { await work(); }
    catch { notify(false, `${label} failed. Please try again.`); }
    finally { actionRef.current = false; setAction(""); }
  };

  const itemLabel = (type: ScriptIncludeType): string => ({
    [ScriptIncludeType.Css]: "Stylesheet",
    [ScriptIncludeType.ClientScript]: "JavaScript",
    [ScriptIncludeType.Robots]: "Robots content",
    [ScriptIncludeType.AI]: "AI content",
  }[type] ?? "Item");

  const serviceRef = useRef<ScriptAndStyleService | null>(null);
  const confirmation = useConfirm();

  const setItemsByType = (type: ScriptIncludeType, updater: (items: Array<IScriptAndStyle>) => Array<IScriptAndStyle>): void => {
    switch (type) {
      case ScriptIncludeType.ClientScript:
        setScripts(updater);
        return;
      case ScriptIncludeType.Robots:
        setRobots(updater);
        return;
      case ScriptIncludeType.AI:
        setAiFiles(updater);
        return;
      case ScriptIncludeType.Css:
      default:
        setStyleItems(updater);
        return;
    }
  };

  const columns: Column<IScriptAndStyle>[] = useMemo(() => [
    { 
      Header: "Description", 
      accessor: "Description" 
    }, 
    {
      Header: "Type",
      Cell: ({ row } : CellProps<IScriptAndStyle>) => {
        switch (row.original.Type) {
          case ScriptIncludeType.Css:
            return <span>Stylesheet</span>;
          case ScriptIncludeType.ClientScript:
            return <span>JavaScript</span>;
          case ScriptIncludeType.Robots:
            return <span>Robots</span>;
          case ScriptIncludeType.AI:
            return <span>AI</span>;
          default:
            return <span>Unknown</span>;
        }
      }
    },
    {
      Header: "Enabled",
      Cell: ({ row } : CellProps<IScriptAndStyle>) => {
        return (
          <button
            type="button"
            className={styles.statusCell}
            disabled={!!action}
            onClick={() => enableScript(row.original, !row.original.Enabled)}
            title={row.original.Enabled ? "Disable item" : "Enable item"}>
            <span className={row.original.Enabled ? styles.statusBadgeOn : styles.statusBadgeOff}>
              {row.original.Enabled ? "Enabled" : "Disabled"}
            </span>
          </button>
        );
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IScriptAndStyle>) => (
        <div className="user-result-actions user-result-actions--single">
          <Button variant="light" disabled={!!action} onClick={() => editScript(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit item">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" disabled={!!action} onClick={() => deleteScript(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete item">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [action]);

  const getTabMeta = (tab: string): ScriptTabMeta => {
    switch (tab) {
      case ScriptTabs.JS:
        return {
          title: "JavaScript",
          sectionTitle: "Site Scripts",
          summary: "Manage the JavaScript files and inline script entries loaded for this site.",
          addText: "Add Script",
          emptyMessage: "No scripts found, click the button above to add a new script",
          type: ScriptIncludeType.ClientScript
        };
      case ScriptTabs.Robots:
        return {
          title: "Robots",
          sectionTitle: "Robots Rules",
          summary: "Manage robots.txt entries and crawler-specific rules for this site.",
          addText: "Add Robots Content",
          emptyMessage: "No robots.txt found, click the button above to add a new robots.txt entry",
          type: ScriptIncludeType.Robots
        };
      case ScriptTabs.AI:
        return {
          title: "AI",
          sectionTitle: "AI Entries",
          summary: "Manage AI-related content files and entries used by this site.",
          addText: "Add AI Content",
          emptyMessage: "No AI files found, click the button above to add a new AI entry",
          type: ScriptIncludeType.AI
        };
      case ScriptTabs.Css:
      default:
        return {
          title: "Stylesheets",
          sectionTitle: "Site Stylesheets",
          summary: "Manage custom stylesheets and CSS entries loaded for this site.",
          addText: "Add Stylesheet",
          emptyMessage: "No custom styles found, click the button above to a new stylesheet",
          type: ScriptIncludeType.Css
        };
    }
  };

  const getActiveData = (): Array<IScriptAndStyle> => {
    switch (activeTab) {
      case ScriptTabs.JS:
        return scripts;
      case ScriptTabs.Robots:
        return robots;
      case ScriptTabs.AI:
        return aiFiles;
      case ScriptTabs.Css:
      default:
        return styleItems;
    }
  };
  
  const addNewItem = (type: ScriptIncludeType) : void => {
    if (serviceRef.current) {
      const newscript = serviceRef.current.newScript(type);
      if (newscript) {
        setSelected(newscript);
        setShowModal(true);
      }
    }
  };

  const editScript = (script: IScriptAndStyle) : void => {
    if (script) {
      setSelected(script);
      setShowModal(true);
    }
  };

  const enableScript = async (script: IScriptAndStyle, enable: boolean) => {
    if (!serviceRef.current) return;
    await runAction("Saving", async () => {
      const res = await serviceRef.current!.saveScript({ ...script, Enabled: enable });
      if (res) {
        setItemsByType(res.Type, (prev) => prev.map((s) => s.Id === res.Id ? res : s));
        notify(true, `${itemLabel(res.Type)} saved.`);
      }
    });
  };

  const closeDeletedSelection = useCloseDeletedSelection(selected, (item) => item.Id, () => closeEditor());

  const deleteScript = async (script: IScriptAndStyle) : Promise<void> => {
    if (serviceRef.current && !actionRef.current) {
      const msgtype = (() => {
        switch (script.Type) {
          case ScriptIncludeType.ClientScript:
            return "JavaScript";
          case ScriptIncludeType.Robots:
            return "Robots content";
          case ScriptIncludeType.AI:
            return "AI content";
          case ScriptIncludeType.Css:
          default:
            return "StyleSheet";
        }
      })();
      const confirmed = await confirmation.requestConfirmation(`Remove the selected ${msgtype}? <b>This action cannot be undone.</b>`, `Remove ${msgtype}`);

      if (confirmed) {
        await runAction("Deleting", async () => {
          const res = await serviceRef.current!.deleteScript(script.Id);
          if (res) {
            setItemsByType(script.Type, (prev) => prev.filter((s) => s.Id !== script.Id));
            closeDeletedSelection(script.Id);
            notify(true, `${itemLabel(script.Type)} deleted.`);
          }
        });
      }
    }
  };

  const saveScript = async (scriptcss: IScriptAndStyle) : Promise<void> => {
    if (serviceRef.current && scriptcss) {
      await runAction("Saving", async () => {
        const res = await serviceRef.current!.saveScript(scriptcss);

        if (res) {
          setItemsByType(res.Type, (prev) => {
            const existingItem = prev.find((s) => s.Id === res.Id);
            if (existingItem) {
              return prev.map((s) => s.Id === res.Id ? res : s);
            }

            return [
              ...prev,
              res
            ];
          });
          closeEditor();
          notify(true, `${itemLabel(res.Type)} saved.`);
        }
      });
    }
  };

  const closeEditor = () : void => {
    setSelected(null);
    setShowModal(false);
  };

  const getScripts = async () : Promise<void> => {
    if (serviceRef.current) {
      const scripts = await serviceRef.current.getScripts();
      setScripts(scripts);
    }
  };

  const getStyles = async () : Promise<void> => {
    if (serviceRef.current) {
      const css = await serviceRef.current.getCss();
      setStyleItems(css);
    }
  };

  const getRobots = async () :  Promise<void> => {
    if (serviceRef.current) {
      const robots = await serviceRef.current.getRobots();
      setRobots(robots);
    }
  };

  const getAI = async () : Promise<void> => {
    if (serviceRef.current) {
      const aifiles = await serviceRef.current.getAI();
      setAiFiles(aifiles);
    }
  };

  const handleTabSelect = (tab: string | null): void => {
    if (tab) {
      setActiveTab(tab);
    }
  };
    
  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new ScriptAndStyleService();

      const loadAll = async (): Promise<void> => {
        try {
          await Promise.all([
            getScripts(),
            getStyles(),
            getRobots(),
            getAI()
          ]);
        } finally {
          setLoading(false);
        }
      };

      loadAll();
    }
  }, []);

  return (
    <>
      {loading && (
        <Row>
          <Col md={12} className="text-center mt-5">
            <Loading Message="loading available site scripts and styles..." SpinSize="5x" key={"LoadingSpinner"}></Loading>
          </Col>
        </Row>
      )}

      {!loading && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className={`app-list-section__body ${styles.tabsWrap}`}>
              <Nav
                variant="pills"
                aria-label="Script and style categories"
                activeKey={activeTab}
                onSelect={handleTabSelect}
                className={styles.tabs}
              >
                <Nav.Item><Nav.Link as="button" type="button" eventKey={ScriptTabs.Css}>Stylesheets</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link as="button" type="button" eventKey={ScriptTabs.JS}>JavaScript</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link as="button" type="button" eventKey={ScriptTabs.Robots}>Robots</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link as="button" type="button" eventKey={ScriptTabs.AI}>AI</Nav.Link></Nav.Item>
              </Nav>
            </div>
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">{getTabMeta(activeTab).sectionTitle}</h2>
                  <p className="app-list-section__summary">{getTabMeta(activeTab).summary}</p>
                </div>
                <div className={styles.headerActions}>
                  {action && <span role="status" className="text-secondary"><Spinner size="sm" aria-hidden="true" /> {action}…</span>}
                  <Button disabled={!!action} variant="primary" onClick={() => addNewItem(getTabMeta(activeTab).type)}>
                    <FontAwesomeIcon icon={faPlus} />
                    {getTabMeta(activeTab).addText}
                  </Button>
                </div>
              </div>
            </div>

            <div className="app-list-section__body">
              <FilteredTable<IScriptAndStyle> 
                columns={columns}
                data={getActiveData()}
                noDataMessage={getTabMeta(activeTab).emptyMessage}
                showAddButton={true}
                addButtonText={getTabMeta(activeTab).addText}
                addButtonIcon={faPlus}
                addButtonHandler={() => addNewItem(getTabMeta(activeTab).type)}
                cellClass="align-middle"
                filterPlaceHolder={`filter ${getTabMeta(activeTab).title.toLowerCase()}...`}
              />
            </div>
          </section>
        </div>
      )}

      {!loading && selected && showModal && (
        <AddUpdateScriptCss
          onClose={closeEditor}
          onSave={saveScript}
          scriptCss={selected}
          type={selected.Type}
          saving={!!action}
        />
      )}
    </>
  );
}

export default ScriptsAndStyles;
